import React, { memo } from "react";
import { Link } from "react-router-dom";
import { useFormContext } from '../forms/form'

interface ButtonProps {
    children?: React.ReactNode;
    className?: string;
    onClick?: () => void;
    disabled?: boolean;
    type?: "button" | "submit" | "reset";
    design?: string;
    size?: "medium" | "large";
    hoverEffect?: boolean;
    to?: string;
    state?: any; // For Link state
    wideOnMobile?: boolean;
}

const baseClasses = `
    bg-gray-300
    text-black
    rounded-md 
    text-base
    leading-8
    h-10
    sm:leading-10
    px-4
    text-nowrap
    select-none
    flex
    items-center
    justify-center
    gap-x-2
    [&>svg]:h-6 [&>svg]:w-6 [&>svg]:!m-0
    [&>span]:!m-0
    capitalize
`

const designClasses: Record<string, string> = {
    primary: "!bg-customSecondary text-white [&>svg]:!fill-white",
    secondary: "!bg-customPrimary text-white [&>svg]:!fill-white",
    danger: "!bg-customDangerColor text-white [&>svg]:!fill-white",
    white: "!bg-white text-black border-black border-2 [&>svg]:!fill-black",
    optional: "!bg-gray-500 text-white [&>svg]:!fill-white",
    skeleton: "!bg-gray-400 animate-pulse w-full md:w-40 text-white cursor-wait [&>svg]:!fill-white",
};

const sizeClasses: Record<"medium" | "large", string> = {
    medium: "",
    large: "h-14 !px-6",
};

const Button: React.FC<ButtonProps> = ({
    children,
    className = "",
    onClick,
    disabled = false,
    type = "button",
    design = "",
    size = "medium",
    hoverEffect = true,
    to,
    state,
    wideOnMobile = false,
}) => {
    const { disableInputs } = useFormContext()
    const isDisabled = disabled || disableInputs
    
    let classes = baseClasses;
    if (design && designClasses[design]) {
        classes += " " + designClasses[design];
    }
    classes += " " + sizeClasses[size];
    if (hoverEffect && !isDisabled) {
        classes += " sm:hover:opacity-90";
    }
    if (isDisabled) {
        classes += " opacity-30 cursor-not-allowed";
    }
    if (wideOnMobile) {
        classes += " w-full max-md:mr-0 md:w-fit";
    } else {
        classes += " !w-fit";
    }
    if (className) {
        classes += " " + className;
    }

    const buttonContent = (
        <button
            type={type}
            className={classes}
            onClick={design !== 'skeleton' ? onClick : undefined}
            disabled={isDisabled}
        >
            {children}
        </button>
    );

    if (to && !isDisabled && design !== 'skeleton') {
        return (
            <Link to={to} tabIndex={isDisabled ? -1 : 0} className={wideOnMobile ? "block w-full md:inline-block md:w-fit" : "inline-block"} state={state}>
                {buttonContent}
            </Link>
        );
    }
    return buttonContent;
};

export default memo(Button);
